"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../constants/config";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const getName = (user) => {
  const first = user?.firstName || "";
  const last = user?.lastName || "";
  return `${first} ${last}`.trim() || user?.fullName || "Unnamed";
};

const getWingName = (user, wingMap) => {
  const namesFromArray = Array.isArray(user?.wings)
    ? user.wings
        .map((item) => (typeof item === "object" ? item?.name : wingMap.get(item)))
        .filter(Boolean)
    : [];
  if (namesFromArray.length > 0) return namesFromArray.join(", ");
  if (!user?.wing) return "Not assigned";
  if (typeof user.wing === "object") return user.wing.name || "Unknown";
  return wingMap.get(user.wing) || "Unknown";
};

const getWingIds = (user) => {
  if (Array.isArray(user?.wings) && user.wings.length > 0) {
    return user.wings
      .map((item) => (typeof item === "object" ? item?._id : item))
      .filter(Boolean)
      .map(String);
  }
  if (!user?.wing) return [];
  const id = typeof user.wing === "object" ? user.wing._id || "" : user.wing;
  return id ? [String(id)] : [];
};

export function WingUserMapping() {
  const [wings, setWings] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedWingId, setSelectedWingId] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [query, setQuery] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      const [wingsRes, teachersRes, staffRes] = await Promise.all([
        axiosInstance.get(API_ENDPOINTS.wings, { headers }),
        axiosInstance.get(API_ENDPOINTS.usersTeachers, { headers, params: { status: "all" } }),
        axiosInstance.get(API_ENDPOINTS.usersStaff, { headers, params: { status: "all" } }),
      ]);

      setWings(wingsRes.data?.data?.wings || []);
      setTeachers((teachersRes.data?.data?.teachers || []).filter((u) => u.role === "teacher"));
      setStaff((staffRes.data?.data?.staff || []).filter((u) => u.role === "staff"));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load wing mapping data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(""), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const wingMap = useMemo(() => new Map(wings.map((w) => [w._id, w.name])), [wings]);

  const users = useMemo(() => {
    const merged = [
      ...teachers.map((u) => ({ ...u, roleLabel: "Teacher" })),
      ...staff.map((u) => ({ ...u, roleLabel: "Staff" })),
    ];
    return merged.filter((user) => {
      const byRole = selectedRole === "all" || user.role === selectedRole;
      const byQuery =
        query.trim() === "" ||
        getName(user).toLowerCase().includes(query.toLowerCase()) ||
        (user.email || "").toLowerCase().includes(query.toLowerCase());
      return byRole && byQuery;
    });
  }, [teachers, staff, selectedRole, query]);

  const visibleUserIds = users.map((u) => u._id);
  const allVisibleSelected =
    visibleUserIds.length > 0 && visibleUserIds.every((id) => selectedUserIds.includes(id));

  const toggleUser = (id) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAllVisible = () => {
    setSelectedUserIds((prev) => {
      if (allVisibleSelected) return prev.filter((id) => !visibleUserIds.includes(id));
      return Array.from(new Set([...prev, ...visibleUserIds]));
    });
  };

  const handleAssign = async () => {
    if (!selectedWingId) {
      setError("Please select a wing first");
      return;
    }
    if (selectedUserIds.length === 0) {
      setError("Please select at least one teacher or staff member");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      const userMap = new Map(users.map((user) => [user._id, user]));
      await Promise.all(
        selectedUserIds.map((userId) => {
          const currentUser = userMap.get(userId);
          if (currentUser?.role === "teacher") {
            const currentWingIds = getWingIds(currentUser);
            const nextWingIds = Array.from(new Set([...currentWingIds, String(selectedWingId)]));
            return axiosInstance.put(
              API_ENDPOINTS.user(userId),
              { wings: nextWingIds, wing: nextWingIds[0] || null },
              { headers }
            );
          }
          return axiosInstance.put(API_ENDPOINTS.user(userId), { wing: selectedWingId }, { headers });
        })
      );

      setSuccess(`Mapped ${selectedUserIds.length} user(s) to ${wingMap.get(selectedWingId) || "selected wing"}`);
      setSelectedUserIds([]);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to map selected users");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold">Teacher/Staff Wing Mapping</h2>
            <p className="text-xs sm:text-sm text-gray-600">Select users and map them to a wing.</p>
          </div>
          <Button variant="outline" onClick={fetchData} disabled={loading || saving} className="w-full sm:w-auto">
            Refresh
          </Button>
        </div>

        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        {success && <Alert><AlertDescription>{success}</AlertDescription></Alert>}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="md:col-span-2">
            <Label htmlFor="wing">Wing</Label>
            <select
              id="wing"
              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={selectedWingId}
              onChange={(e) => setSelectedWingId(e.target.value)}
              disabled={loading || saving}
            >
              <option value="">Select wing</option>
              {wings.map((wing) => (
                <option key={wing._id} value={wing._id}>{wing.name}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="role">User Type</Label>
            <select
              id="role"
              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value);
                setSelectedUserIds([]);
              }}
              disabled={loading || saving}
            >
              <option value="all">All</option>
              <option value="teacher">Teacher</option>
              <option value="staff">Staff</option>
            </select>
          </div>

          <div>
            <Label htmlFor="search">Search</Label>
            <Input
              id="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name or email"
              disabled={loading || saving}
            />
          </div>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <label className="inline-flex items-center gap-2 text-xs sm:text-sm text-gray-700">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleAllVisible}
              disabled={loading || saving || visibleUserIds.length === 0}
            />
            Select all visible users ({visibleUserIds.length})
          </label>
          <Button
            onClick={handleAssign}
            disabled={loading || saving || selectedUserIds.length === 0 || !selectedWingId}
            className="w-full sm:w-auto"
          >
            {saving ? "Mapping..." : `Map Selected (${selectedUserIds.length})`}
          </Button>
        </div>

        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">Select</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Current Wing</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(user._id)}
                        onChange={() => toggleUser(user._id)}
                        disabled={loading || saving}
                      />
                    </TableCell>
                    <TableCell>{getName(user)}</TableCell>
                    <TableCell>{user.roleLabel}</TableCell>
                    <TableCell>{user.email || "-"}</TableCell>
                    <TableCell>{getWingName(user, wingMap)}</TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                      {loading ? "Loading users..." : "No users found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
